import { useCallback, useEffect, useMemo, useState } from "react";

import { describeCoreError } from "../../core-api/errors";
import { AIWorkbench, type ReviewedAIRun } from "../ai-platform/AIWorkbench";
import { roleLabel, statusLabel } from "../../localization/zh-CN";
import {
  confirmAIReview,
  continueWithoutAI,
  collectRealShadowBatch0,
  loadLocalUatAccounts,
  loadRealShadowInquiries,
  loadShadowFlow,
  performShadowAction,
  type ShadowFlowStage,
  type ShadowFlowView,
  type LocalUatAccountDirectory,
  type RealShadowListItem,
} from "./api";
import "./shadow-flow.css";

const STAGES: Array<[ShadowFlowStage, string]> = [
  ["INQUIRY_READY", "影子询单"],
  ["AI_REVIEWED", "人工 AI 复核"],
  ["SALES_ASSIGNED", "销售分配"],
  ["SALES_ACCEPTED", "销售接受"],
  ["CASE_CREATED", "客户与案件"],
  ["SOURCING_ASSIGNED", "查货分配"],
  ["SOURCING_ACCEPTED", "查货接受"],
  ["SUPPLIER_RESULT_RECORDED", "供应商结果"],
  ["PRICING_READY", "定价交接"],
  ["QUOTE_READY", "客户报价"],
  ["WAITING_APPROVAL", "等待审批"],
  ["APPROVED", "已审批"],
  ["SENT_SYNTHETIC", "合成发送"],
];

const REQUIRED_AI = [
  "inquiry_quality_judgment_v1",
  "customer_judgment_v1",
  "basic_chemical_extraction_v1",
  "routing_recommendation_v1",
] as const;

export function ShadowFlowWorkspace({
  workspaceId,
}: {
  readonly workspaceId: string;
}) {
  const [flow, setFlow] = useState<ShadowFlowView>();
  const [reviews, setReviews] = useState<Record<string, ReviewedAIRun>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [accountDirectory, setAccountDirectory] =
    useState<LocalUatAccountDirectory>();
  const [realBatchResult, setRealBatchResult] = useState<{
    status: string;
    created: number;
    sourceWrites: 0;
    businessMutations: 0;
  }>();
  const [realBatchBusy, setRealBatchBusy] = useState(false);
  const [realBatchError, setRealBatchError] = useState("");
  const [realShadowItems, setRealShadowItems] = useState<RealShadowListItem[]>(
    [],
  );

  const refresh = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError("");
    try {
      setFlow(await loadShadowFlow(signal));
    } catch (failure) {
      if (!signal?.aborted) setError(describeCoreError(failure));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    void loadLocalUatAccounts(controller.signal)
      .then(setAccountDirectory)
      .catch(() => undefined);
    void loadRealShadowInquiries(controller.signal)
      .then(({ items }) => setRealShadowItems(items))
      .catch(() => undefined);
    return () => controller.abort();
  }, [refresh]);

  const runAction = async (
    action: Parameters<typeof performShadowAction>[1],
  ) => {
    if (!flow) return;
    setBusy(true);
    setError("");
    try {
      setFlow(await performShadowAction(flow.id, action));
    } catch (failure) {
      setError(describeCoreError(failure));
    } finally {
      setBusy(false);
    }
  };

  const registerReview = (review: ReviewedAIRun) =>
    setReviews((current) => ({ ...current, [review.useCaseKey]: review }));

  const completeAI = async () => {
    if (!flow) return;
    setBusy(true);
    setError("");
    try {
      setFlow(
        await confirmAIReview(
          flow.id,
          REQUIRED_AI.map((key) => reviews[key]!.invocationId),
        ),
      );
    } catch (failure) {
      setError(describeCoreError(failure));
    } finally {
      setBusy(false);
    }
  };

  const manualContinue = async () => {
    if (!flow) return;
    setBusy(true);
    setError("");
    try {
      setFlow(await continueWithoutAI(flow.id));
    } catch (failure) {
      setError(describeCoreError(failure));
    } finally {
      setBusy(false);
    }
  };

  const runRealBatch0 = async () => {
    setRealBatchBusy(true);
    setRealBatchError("");
    try {
      const result = await collectRealShadowBatch0();
      setRealBatchResult(result);
      const refreshed = await loadRealShadowInquiries();
      setRealShadowItems(refreshed.items);
    } catch (failure) {
      setRealBatchError(describeCoreError(failure));
    } finally {
      setRealBatchBusy(false);
    }
  };

  const reviewCount = REQUIRED_AI.filter((key) => reviews[key]).length;
  const hasChangedReview = Object.values(reviews).some(
    ({ decision }) => decision === "ACCEPT_WITH_CHANGES",
  );
  const next = useMemo(() => (flow ? nextAction(flow) : undefined), [flow]);

  if (loading) return <div className="shadow-state">正在读取影子流程…</div>;
  if (!flow)
    return (
      <div className="shadow-state is-error">
        <p>{error || "当前工作空间没有可验收的合成影子询单。"}</p>
        <RealBatch0Panel
          busy={realBatchBusy}
          error={realBatchError}
          result={realBatchResult}
          snapshot={realShadowItems[0]}
          onRun={runRealBatch0}
        />
      </div>
    );

  const inquiryText = [
    `产品：${flow.shadowInquiry.product ?? "待确认"}`,
    `CAS：${flow.shadowInquiry.cas ?? "待确认"}`,
    `数量：${flow.shadowInquiry.quantity ?? "待确认"}`,
    `规格：${flow.shadowInquiry.specification ?? "待确认"}`,
    `组织：${flow.shadowInquiry.companyName ?? "合成组织"}`,
    "这是经过脱敏的合成影子询单。不得推断或联系原始客户。",
  ].join("\n");

  return (
    <div className="shadow-workspace">
      <RealBatch0Panel
        busy={realBatchBusy}
        error={realBatchError}
        result={realBatchResult}
        snapshot={realShadowItems[0]}
        onRun={runRealBatch0}
      />
      <section className="shadow-hero">
        <div>
          <span className="shadow-kicker">CRM-INT-26B · 隔离业务模拟</span>
          <h2>AI 影子流程</h2>
          <p>用脱敏副本完成询单到合成报价回复；原询单保持只读。</p>
        </div>
        <div className="shadow-badges">
          <span>合成数据</span>
          <span>禁止联系</span>
          <span>仅限本地邮件</span>
        </div>
      </section>

      {error ? <div className="shadow-alert">{error}</div> : null}

      <section className="shadow-progress" aria-label="影子业务流程">
        {STAGES.map(([stage, label], index) => {
          const current = STAGES.findIndex(([value]) => value === flow.stage);
          return (
            <div
              key={stage}
              className={
                index < current
                  ? "is-done"
                  : index === current
                    ? "is-current"
                    : ""
              }
            >
              <span>{index < current ? "✓" : index + 1}</span>
              <small>{label}</small>
            </div>
          );
        })}
      </section>

      <div className="shadow-grid">
        <section className="shadow-card">
          <header>
            <div>
              <span className="shadow-label">当前阶段</span>
              <h3>{stageLabel(flow.stage)}</h3>
            </div>
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={busy}
            >
              刷新
            </button>
          </header>
          <dl className="shadow-facts">
            <Fact label="影子询单" value={flow.shadowInquiry.publicId} />
            <Fact label="产品" value={flow.shadowInquiry.product ?? "待确认"} />
            <Fact label="CAS" value={flow.shadowInquiry.cas ?? "待确认"} />
            <Fact
              label="数量"
              value={flow.shadowInquiry.quantity ?? "待确认"}
            />
            <Fact
              label="规格"
              value={flow.shadowInquiry.specification ?? "待确认"}
            />
            <Fact
              label="客户"
              value={flow.shadowInquiry.contactName ?? "合成客户"}
            />
            <Fact label="邮箱" value={flow.shadowInquiry.email} />
            <Fact
              label="原询单保护"
              value={
                flow.originalInquiryUnchanged
                  ? "Hash 一致 · 未修改"
                  : "校验失败"
              }
            />
          </dl>
          {next ? (
            <div className="shadow-next">
              <div>
                <strong>{next.label}</strong>
                <small>{next.help}</small>
              </div>
              {next.enabled ? (
                <button
                  className="shadow-primary"
                  type="button"
                  disabled={busy}
                  onClick={() => void runAction(next.action)}
                >
                  {busy ? "处理中…" : next.button}
                </button>
              ) : (
                <span>请切换到对应虚拟账号</span>
              )}
            </div>
          ) : null}
        </section>

        <section className="shadow-card">
          <header>
            <div>
              <span className="shadow-label">业务记录</span>
              <h3>已生成记录</h3>
            </div>
          </header>
          <dl className="shadow-facts is-ids">
            <Fact label="案件" value={flow.caseId ?? "尚未创建"} />
            <Fact label="供应商询价" value={flow.supplierRfqId ?? "尚未创建"} />
            <Fact label="成本快照" value={flow.costSnapshotId ?? "尚未生成"} />
            <Fact label="客户报价" value={flow.customerQuoteId ?? "尚未创建"} />
            <Fact label="审批" value={flow.approvalRequestId ?? "尚未提交"} />
            <Fact
              label="合成发送"
              value={flow.syntheticDeliveryId ?? "尚未发送"}
            />
          </dl>
          {flow.supplierOffer ? (
            <div className="shadow-sensitive">
              <strong>采购视图</strong>
              {flow.supplierOffer.supplier ? (
                <span>供应商：{flow.supplierOffer.supplier.displayName}</span>
              ) : null}
              {flow.supplierOffer.lines[0]?.unitCost ? (
                <span>
                  供应商单价：{flow.supplierOffer.currency}{" "}
                  {flow.supplierOffer.lines[0].unitCost}
                </span>
              ) : null}
              <span>交期：{flow.supplierOffer.leadTimeDays ?? 14} 天</span>
            </div>
          ) : null}
        </section>
      </div>

      {flow.stage === "INQUIRY_READY" && flow.permissions.canReviewAI ? (
        <section className="shadow-ai-section">
          <div className="shadow-ai-summary">
            <div>
              <strong>人工 AI 判断：{reviewCount}/4</strong>
              <span>依次运行并复核四个用例，至少一次选择“修改后接受”。</span>
            </div>
            <div className="shadow-ai-actions">
              <button
                type="button"
                disabled={busy || reviewCount !== 4 || !hasChangedReview}
                onClick={() => void completeAI()}
              >
                确认四项人工复核
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void manualContinue()}
              >
                AI 失败，转纯人工继续
              </button>
            </div>
          </div>
          <AIWorkbench
            embedded
            workspaceId={workspaceId}
            subjectId={flow.shadowInquiry.id}
            inquiryText={inquiryText}
            onReviewed={registerReview}
          />
        </section>
      ) : null}

      {flow.pricingHandoff ? (
        <section className="shadow-card shadow-pricing">
          <header>
            <div>
              <span className="shadow-label">核心定价交接</span>
              <h3>定价与销售交接</h3>
            </div>
            <span className="shadow-readonly">只读 · 核心服务计算</span>
          </header>
          <div className="shadow-price-equation">
            <div>
              <small>成本基准</small>
              <strong>CNY {flow.pricingHandoff.approvedCostBasisCny}</strong>
            </div>
            <b>× {flow.pricingHandoff.markupFactor}</b>
            <div>
              <small>销售价格</small>
              <strong>CNY {flow.pricingHandoff.sellingPriceCny}</strong>
            </div>
            <b>÷ {flow.pricingHandoff.fxSnapshot}</b>
            <div>
              <small>客户单价</small>
              <strong>USD {flow.pricingHandoff.customerUnitPriceUsd}</strong>
            </div>
          </div>
          <p>
            {flow.pricingHandoff.leadTime} · 有效期{" "}
            {flow.pricingHandoff.validity} ·{" "}
            {flow.pricingHandoff.roundingPolicy}
          </p>
        </section>
      ) : null}

      {accountDirectory ? (
        <section className="shadow-card shadow-accounts">
          <header>
            <div>
              <span className="shadow-label">本地 UAT 访问</span>
              <h3>固定账号、角色与权限</h3>
            </div>
            <span className="shadow-readonly">管理员只读目录</span>
          </header>
          <p>
            此处仅展示正式服务器权限。角色查看不会伪造执行者；需要验证导航时请使用对应固定账号登录。
          </p>
          <div className="shadow-account-list">
            {accountDirectory.accounts.map((account) => (
              <details key={account.username}>
                <summary>
                  <span>
                    <strong>{account.username}</strong>
                    <small>{account.roles.map(roleLabel).join(" · ")}</small>
                  </span>
                  <span>
                    {account.readOnly ? "只读" : statusLabel(account.status)}
                  </span>
                </summary>
                <div className="shadow-permission-list">
                  {account.permissions.map((permission) => (
                    <code key={permission}>{permission}</code>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </section>
      ) : null}

      <section className="shadow-card shadow-timeline">
        <header>
          <div>
            <span className="shadow-label">仅追加证据</span>
            <h3>业务时间线</h3>
          </div>
          <span>{flow.timeline.length} 个事件</span>
        </header>
        <ol>
          {flow.timeline.map((event) => (
            <li key={event.id}>
              <span>{event.sequence}</span>
              <div>
                <strong>{stageLabel(event.action as ShadowFlowStage)}</strong>
                <small>
                  {new Date(event.occurredAt).toLocaleString("zh-CN")}
                </small>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function RealBatch0Panel({
  busy,
  error,
  result,
  snapshot,
  onRun,
}: {
  busy: boolean;
  error: string;
  result?: {
    status: string;
    created: number;
    sourceWrites: 0;
    businessMutations: 0;
  };
  snapshot?: RealShadowListItem;
  onRun: () => Promise<void>;
}) {
  const validation = snapshot?.fieldValidation ?? [];
  const countState = (state: string) =>
    validation.filter((item) => item.state === state).length;
  const recommendation = snapshot?.recommendation;
  return (
    <section className="shadow-card" aria-label="真实询单影子批次">
      <header>
        <div>
          <span className="shadow-label">真实数据 · 只读影子</span>
          <h2>BenchChem 真实询单 Batch 0</h2>
        </div>
        <span className="shadow-readonly">只读 · 单条 · 确定性</span>
      </header>
      <p>
        仅读取并保存 1 条真实询单影子快照；不启用 AI，不创建或修改正式业务记录。
      </p>
      <button
        type="button"
        className="shadow-primary"
        disabled={busy || Boolean(snapshot)}
        onClick={() => void onRun()}
      >
        {busy
          ? "处理中…"
          : snapshot
            ? "Batch 0 已执行"
            : "执行 Batch 0（1 条）"}
      </button>
      {error ? <div className="shadow-alert">{error}</div> : null}
      {result ? (
        <dl className="shadow-facts">
          <Fact label="执行状态" value={statusLabel(result.status)} />
          <Fact label="创建快照" value={String(result.created)} />
          <Fact label="Source 写入" value={String(result.sourceWrites)} />
          <Fact label="业务变更" value={String(result.businessMutations)} />
        </dl>
      ) : null}
      {snapshot ? (
        <>
          <dl className="shadow-facts">
            <Fact label="影子引用" value={snapshot.anonymousReference} />
            <Fact label="站点" value={statusLabel(snapshot.sourceSite)} />
            <Fact
              label="邮箱类别"
              value={emailCategoryLabel(snapshot.emailCategory)}
            />
            <Fact label="产品" value={snapshot.product ? "已提供" : "缺失"} />
            <Fact label="CAS" value={snapshot.cas ? "已提供" : "缺失"} />
            <Fact label="数量" value={snapshot.quantity ? "已提供" : "缺失"} />
          </dl>
          <dl className="shadow-facts">
            <Fact label="已验证" value={String(countState("VERIFIED"))} />
            <Fact
              label="内部解决"
              value={String(countState("RESOLVED_FROM_INTERNAL_DATA"))}
            />
            <Fact
              label="非阻塞缺失"
              value={String(countState("MISSING_NON_BLOCKING"))}
            />
            <Fact
              label="阻塞缺失"
              value={String(countState("MISSING_BLOCKING"))}
            />
            <Fact label="冲突" value={String(countState("CONFLICT"))} />
          </dl>
          <dl className="shadow-facts">
            <Fact
              label="组织解析"
              value={`${resolutionLabel(snapshot.organizationResolution.status)} · ${statusLabel(snapshot.organizationResolution.organizationType)} · ${statusLabel(snapshot.organizationResolution.confidence)}`}
            />
            <Fact
              label="客户等级候选"
              value={`${statusLabel(snapshot.customerResolution.recommendedGrade)} · ${statusLabel(snapshot.customerResolution.confidence)}`}
            />
            <Fact label="风险" value={recommendation?.risk.level ?? "未生成"} />
            <Fact
              label="案件候选"
              value={String(recommendation?.caseCandidates.length ?? 0)}
            />
            <Fact
              label="查货历史"
              value={statusLabel(
                recommendation?.sourcingHistory.type ?? "NO_HISTORY",
              )}
            />
          </dl>
          <div className="shadow-next">
            <div>
              <strong>确定性影子分析</strong>
              <small>
                建议：
                {statusLabel(
                  recommendation?.shadowRecommendationType ?? "MANUAL_TRIAGE",
                )}{" "}
                · AI 未启用，本阶段按计划关闭
              </small>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}

function emailCategoryLabel(
  category: RealShadowListItem["emailCategory"],
): string {
  return {
    PERSONAL: "个人邮箱",
    ACADEMIC_INSTITUTIONAL: "学术 / 机构邮箱",
    CORPORATE: "企业邮箱",
    UNKNOWN: "未知",
  }[category];
}

function resolutionLabel(
  status: RealShadowListItem["organizationResolution"]["status"],
): string {
  return {
    RESOLVED: "已解析",
    CANDIDATE: "候选",
    CONFLICT: "冲突",
    UNKNOWN: "未知",
  }[status];
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd title={value}>{value}</dd>
    </div>
  );
}

function stageLabel(stage: ShadowFlowStage): string {
  return (
    STAGES.find(([value]) => value === stage)?.[1] ?? `未识别阶段（${stage}）`
  );
}

function nextAction(flow: ShadowFlowView):
  | {
      action: Parameters<typeof performShadowAction>[1];
      label: string;
      help: string;
      button: string;
      enabled: boolean;
    }
  | undefined {
  const p = flow.permissions;
  const actions: Partial<
    Record<
      ShadowFlowStage,
      {
        action: Parameters<typeof performShadowAction>[1];
        label: string;
        help: string;
        button: string;
        enabled: boolean;
      }
    >
  > = {
    AI_REVIEWED: {
      action: "assign-sales",
      label: "下一步：分配销售",
      help: "由分诊专员分配给 UAT 销售。",
      button: "分配销售",
      enabled: p.canDispatch,
    },
    SALES_ASSIGNED: {
      action: "accept-sales",
      label: "下一步：销售接受",
      help: "仅被分配的 UAT 销售可以接受。",
      button: "接受销售工作",
      enabled: p.canAcceptSales,
    },
    SALES_ACCEPTED: {
      action: "create-case",
      label: "下一步：创建客户与案件",
      help: "创建合成客户、联系人和案件。",
      button: "创建客户 / 联系人 / 案件",
      enabled: p.canCreateCase,
    },
    CASE_CREATED: {
      action: "assign-sourcing",
      label: "下一步：分配查货",
      help: "销售从案件发起合成 RFQ 工作项。",
      button: "分配查货",
      enabled: p.canAssignSourcing,
    },
    SOURCING_ASSIGNED: {
      action: "accept-sourcing",
      label: "下一步：查货接受",
      help: "仅被分配的 UAT 寻源人员可以接受。",
      button: "接受查货工作",
      enabled: p.canAcceptSourcing,
    },
    SOURCING_ACCEPTED: {
      action: "record-supplier-result",
      label: "下一步：录入供应商结果",
      help: "使用固定合成数据，不会外发 RFQ。",
      button: "录入供应商报价",
      enabled: p.canRecordSupplierQuote,
    },
    SUPPLIER_RESULT_RECORDED: {
      action: "prepare-pricing",
      label: "下一步：生成定价交接",
      help: "核心服务创建不可变成本快照并计算价格。",
      button: "生成定价交接",
      enabled: p.canPreparePricing,
    },
    PRICING_READY: {
      action: "create-customer-quote",
      label: "下一步：确认客户报价",
      help: "销售确认只读的核心价格事实。",
      button: "创建客户报价",
      enabled: p.canCreateQuote,
    },
    QUOTE_READY: {
      action: "submit-approval",
      label: "下一步：提交审批",
      help: "提交给指定 UAT 审批人。",
      button: "提交报价审批",
      enabled: p.canSubmitApproval,
    },
    WAITING_APPROVAL: {
      action: "approve",
      label: "下一步：审批",
      help: "仅指定 UAT 审批人可以批准。",
      button: "批准合成报价",
      enabled: p.canApprove,
    },
    APPROVED: {
      action: "synthetic-send",
      label: "下一步：合成发送",
      help: "仅发送到本地邮件接收器的 .invalid 地址。",
      button: "执行合成发送",
      enabled: p.canSyntheticSend,
    },
  };
  return actions[flow.stage];
}
